import React, { useState, useCallback, useEffect } from 'react'
import Select, { components } from 'react-select'
import findIndex from 'lodash/findIndex'
import cn from 'classnames'
import Checkbox from '../Checkbox/Checkbox'
import FieldLabel from '../FieldLabel/FieldLabel'
import MultiValueContainers from './MultiValueContainers'
import Menus from './Menus'
import MultiValues from './MultiValue'
import ContentEditor from './ContentEditor'
import Icon from '../Icon2/Icon'
import './Select.css'

const SingleValue = (props: any) => {
  const options = props.getValue()
  const { updateOption, ...rest } = props
  return (
    <components.SingleValue {...rest}>
      {options.map((option: any, index: number) => {
        return <ContentEditor key={index} value={option.label} option={option} updateOption={updateOption} />
      })}
    </components.SingleValue>
  )
}

const MultiValueLabel = (props: any) => {
  const { updateOption, ...rest } = props
  return (
    <components.MultiValueLabel {...rest}>
      <ContentEditor value={rest.data.label} option={rest.data} updateOption={updateOption} />
    </components.MultiValueLabel>
  )
}

const MultiValueContainer = ({ children, ...props }) => {
  return <MultiValueContainers children={children} {...props} />
}

const GetPortalValueContainer = ({ children, hasValue, ...props }) => {
  const [selectedItems, otherChildren] = children
  let newSelectedItemsWithtitle = selectedItems
  if (selectedItems?.length) {
    newSelectedItemsWithtitle = selectedItems?.map((iSelected, index) => {
      return <div data-test-id={`cs-selectected-pills-${index}`} key={index} title={iSelected?.props?.data?.label && typeof iSelected.props.data.label === 'string' ? iSelected.props.data.label : undefined}>{iSelected}</div>
    })
  }
  return <components.ValueContainer {...props}>{newSelectedItemsWithtitle}{otherChildren}</components.ValueContainer>
}


const getValueContainer = ({ children, hasValue, ...props }) => {
  const { isValueContainer, multiDisplayLimit, version, showAllSelectedItems, setShowAllSelectedItems } = props.selectProps;

  const [selectedItems, otherChildren] = children

  let newSelectedItemsWithtitle;
  let itemsToDisplay = selectedItems;
  let overflowCounter;

  if (Array.isArray(selectedItems)) {
    newSelectedItemsWithtitle = selectedItems

    if (version === 'v2') {
      newSelectedItemsWithtitle = selectedItems.map((iSelected, index) => {
        return <div data-test-id={`cs-selectected-pills-${index}`} key={index} title={iSelected?.props?.data?.label && typeof iSelected.props.data.label === 'string' ? iSelected.props.data.label : undefined}>{iSelected}</div>
      })
    }

    itemsToDisplay = showAllSelectedItems ? newSelectedItemsWithtitle : newSelectedItemsWithtitle.slice(0, multiDisplayLimit)
    overflowCounter = selectedItems.length - multiDisplayLimit
  }

  if (isValueContainer) {
    return <components.ValueContainer {...props}>
      <div className="Select__value-container--custom">
        {itemsToDisplay}
        {hasValue && !showAllSelectedItems && overflowCounter > 0 && (
          <button
            className="Select__see-more-button"
            id="see-more-button"
            data-test-id="see-more-button"
            onClick={() => {
              setShowAllSelectedItems(true)
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}>
            {`+${overflowCounter} More`}
          </button>
        )}
        {hasValue && showAllSelectedItems && overflowCounter > 0 && (
          <button
            className="Select__see-less-button"
            id="see-less-button"
            data-test-id="see-less-button"
            onClick={() => {
              setShowAllSelectedItems(false)
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}>
            {`See Less`}
          </button>
        )}
        {otherChildren}
      </div>

    </components.ValueContainer>
  }

  return <components.MultiValueContainer {...props}>{children}</components.MultiValueContainer>
}


const IndeterminateCheckBox = ({ indeterminate, checked, ...props }) => {
  const resolvedRef: any = React.useRef()
  useEffect(() => {
    if (resolvedRef.current) {
      if (indeterminate) {
        resolvedRef.current.checked = undefined
        resolvedRef.current.indeterminate = indeterminate
      }
      else {
        resolvedRef.current.indeterminate = false;
        resolvedRef.current.checked = checked
      }

    }
  }, [resolvedRef.current, indeterminate, checked])
  return (
    <Checkbox inputRef={resolvedRef} {...props} />
  )
}


const SelectAllMenuWrapper = (props) => {
  return (
    <components.MenuList {...props}>
      <div className='Select-all__options'>
        {props.children}
      </div>
    </components.MenuList>
  )
}

/** Returns a custom option component to be sent to the react-select component */
const Option = ({ classname }: { classname?: string }) => {
  return (props: any) => {
    if (props.isMulti && props.selectProps.noCheckBox && props.selectProps.isNested) {
      return (
        <components.Option {...props} className={classname}>
          <div
            data-test-id={props.data.testId || 'cs-select-element'}
            style={{ paddingLeft: `${props.data.depth * 10}px` }}>
            {props.children}
          </div>
        </components.Option>
      )
    }

    if (props.isMulti) {
      let flag = false
      let optionIdx = findIndex(props.options, { value: props.value })
      if (optionIdx > -1 && optionIdx !== 0 && props.data.depth === 0) {
        flag = true
      }
      return (
        <components.Option {...props}>
          {props.selectProps.isNested ? (
            <div
              data-test-id={props.data.testId || 'cs-select-element'}
              style={{ paddingLeft: `${props.data.depth * 10}px` }}>
              <Checkbox
                checked={props.isSelected}
                label={props.children}
                isLabelFullWidth={true}
                tooltipContent={typeof props.data.label === 'string' ? props.data.label : undefined}
              />
            </div>
          ) : (
            <>
              {props.data.isSelectAllComponent ? (
                <IndeterminateCheckBox
                  label={props.data.label}
                  checked={props.data.presentState === 'all'}
                  indeterminate={props.data.presentState === 'some'}
                  testId={'cs-select-selectAll-option'}
                  className={'Select__all-checkbox'}
                />
              ) : (
                <Checkbox
                  checked={props.isSelected}
                  label={props.data.secondaryLabel ? (
                    <>
                      {props.children}
                      {props.data.secondaryLabel}
                    </>
                  ) : props.children}
                  isLabelFullWidth={true}
                  testId={props.data.testId || 'cs-select-element'}
                  tooltipContent={typeof props.data.label === 'string' ? props.data.label : undefined}
                />
              )}
            </>
          )}
        </components.Option>
      )
    }
    if (props.selectProps.isNested && !props.isMulti) {
      let flag = false
      let optionIdx = findIndex(props.options, { value: props.value })
      if (optionIdx > -1 && optionIdx !== 0 && props.data.depth === 0) {
        flag = true
      }

      return (
        <components.Option {...props}>
          <div
            data-test-id={props.data.testId || 'cs-select-element'}
            style={{ paddingLeft: `${props.data.depth * 10}px` }}>
            {props.children}
          </div>
        </components.Option>
      )
    }
    // return <components.Option {...props} className={classname} />
    return (
      <div data-test-id={props.data.testId || 'cs-select-element'} title={typeof props.label === 'string' ? props.label : undefined}>
        <components.Option {...props} className={classname} />
      </div>
    )
  }
}

const DropDownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon
        icon="CaretDownNew"
        version='v2'
        size="medium"
        className='select-caret-down'
        testId='cs-select-caret-down'
      />
    </components.DropdownIndicator>
  )
}

const MultiValueRemoveComponent = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <Icon
        icon="Cancel"
        version='v2'
        className='mr-8 flex-v-center'
        height={16}
        width={16}
        testId='cs-select-remove'
      />
    </components.MultiValueRemove>
  )
}

const ClearIndicatorComponent = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <Icon
        icon="Cancel"
        version='v2'
        size="medium"
        className='select-clear-all'
        testId='cs-select-clear-all'
      />
    </components.ClearIndicator>
  )
}

const customComponents = ({
  updateOption,
  canEditOption,
  showCount,
  addOption,
  optionClassname,
  decorators = {},
  isMulti,
  multiDisplayLimit,
  menuStyle,
  menuClassName,
  version,
  isSelectAll,
}: {
  isMulti: boolean
  showAllSelectedItems: boolean
  setShowAllSelectedItems: React.Dispatch<React.SetStateAction<boolean>>
  multiDisplayLimit: number
  menuStyle?: Object
  menuClassName?: string
  isSelectAll?: boolean
  [key: string]: any
}) => {
  let selectObj = {};
  if (version === 'v2') {
    selectObj = {
      ...selectObj,
      DropdownIndicator: (props) => <DropDownIndicator {...props} />,
      ClearIndicator: (props) => <ClearIndicatorComponent {...props} />,
      MultiValueRemove: (props) => <MultiValueRemoveComponent {...props} />
    }
  }
  if (canEditOption) {
    selectObj = {
      ...selectObj,
      SingleValue: (props: any) => <SingleValue {...props} updateOption={updateOption} />,
      MultiValueLabel: (props: any) => <MultiValueLabel {...props} updateOption={updateOption} />,
      Option: Option({ classname: optionClassname })
    }
    return selectObj
  } else if (showCount && !addOption) {
    selectObj = {
      ...selectObj,
      MultiValueContainer: (props) => <MultiValueContainers {...props} />,
      Option: Option({ classname: optionClassname })
    }
    return selectObj
  } else if (addOption) {
    if (MultiValueContainer) {
      if (isMulti && multiDisplayLimit) {
        selectObj = {
          ...selectObj,
          ValueContainer: getValueContainer,
          Option: Option({ classname: optionClassname }),
          Menu: (props) => <Menus {...props} menuStyle={menuStyle} menuClassName={menuClassName} />
        }
        return selectObj
      }
      selectObj = {
        ...selectObj,
        MultiValue: (props) => <MultiValues {...props} />,
        Option: Option({ classname: optionClassname }),
        Menu: (props) => <Menus {...props} menuStyle={menuStyle} menuClassName={menuClassName} />
      }
      return selectObj
    } else {
      selectObj = {
        ...selectObj,
        Option: Option({ classname: optionClassname }),
        Menu: (props) => <Menus {...props} menuStyle={menuStyle} menuClassName={menuClassName} />
      }
      return selectObj
    }
  } else if (Object.keys(decorators).length) {
    const { CustomDropdownIndicator } = decorators
    return {
      Option: Option({ classname: optionClassname }),
      DropdownIndicator: (props) => {
        return (
          <components.DropdownIndicator {...props}>
            {CustomDropdownIndicator ? <CustomDropdownIndicator /> : props.children}
          </components.DropdownIndicator>
        )
      }
    }
  } else {
    if (isMulti && isSelectAll) {
      selectObj = {
        ...selectObj,
        MenuList: SelectAllMenuWrapper
      }
    }

    if (isMulti && multiDisplayLimit) {
      selectObj = {
        ...selectObj,
        Option: Option({ classname: optionClassname }),
        ValueContainer: getValueContainer
      }
      return selectObj
    }
    if (version === 'v2' && isMulti) {
      selectObj = {
        ...selectObj,
        ValueContainer: GetPortalValueContainer
      }
    }



    selectObj = {
      ...selectObj,
      Option: Option({ classname: optionClassname }),
    }
    return selectObj
  }
}

export type ISelectProps = {
  width?: string
  maxWidth?: string
  minWidth?: string
  name?: string
  isDisabled?: boolean
  isClearable?: boolean
  isMulti?: boolean
  isSearchable?: boolean
  placeholder?: string
  hideSelectedOptions?: boolean
  menuShouldScrollIntoView?: boolean
  menuPlacement?: 'auto' | 'bottom' | 'top'
  menuIsOpen?: boolean
  maxMenuHeight?: number
  minMenuHeight?: number
  onChange: Function
  onBlur?: any
  options: any[]
  value: any
  canEditOption?: boolean
  updateOption?: (IUpdateOption: { label: any; id: any }) => void
  selectLabel?: string
  isNested?: boolean
  showCount?: boolean
  selectedLabel?: string
  addOption?: Function
  hasAddOption?: boolean
  addOptionText?: React.ReactNode | Element
  getOptionLabel?: any
  getOptionValue?: any
  noCheckBox?: boolean
  showMore?: boolean
  className?: string
  decorators?: object
  noOptionsMessage?: any
  error?: Boolean
  optionClassname?: string,
  id?: string,
  testId?: string,
  filterOption?: Function,
  isOptionSelected?: Function,
  isOptionDisabled?: Function,
  multiDisplayLimit?: number,
  menuPortalTarget?: any,
  styles?: any,
  menuStyle?: object,
  menuClassName?: string,
  type?: 'primary' | 'secondary',
  onMenuOpen?: Function,
  onMenuClose?: Function,
  version?: "v2",
  tabSelectsValue?: boolean,
  onKeyDown?: any,
  ref?: React.Ref<any>,
  isSelectAll?: boolean,
}

const SelectField: React.FunctionComponent<ISelectProps> = React.forwardRef((props, ref) => {

  const {
    testId = "cs-select",
    maxMenuHeight = 283,
    isDisabled = false,
    isClearable = false,
    isMulti = false,
    isSearchable = false,
    hideSelectedOptions = false,
    tabSelectsValue = true
  } = props;

  const [showAllSelectedItems, setShowAllSelectedItems] = useState(false);

  const allComponents = customComponents({
    updateOption: props.updateOption,
    canEditOption: props.canEditOption,
    showCount: props.showCount,
    addOption: props.hasAddOption,
    showMore: props.showMore,
    decorators: props.decorators,
    optionClassname: props.optionClassname,
    isMulti: isMulti,
    showAllSelectedItems: showAllSelectedItems,
    setShowAllSelectedItems: setShowAllSelectedItems,
    multiDisplayLimit: props.multiDisplayLimit,
    menuStyle: props.menuStyle,
    menuClassName: props.menuClassName,
    version: props.version,
    isSelectAll: props.isSelectAll
  })

  let widthStyleObj: any = {}
  if (props.width) {
    widthStyleObj = { width: props.width }
  } else {
    widthStyleObj = { maxWidth: props.maxWidth || '500px', minWidth: props.minWidth || '200px' }
  }

  const handleChange = useCallback(
    (newValue: any) => {
      // checking whether the newValue is SelectAll
      if (newValue?.length > 0 && newValue[newValue.length - 1]?.isSelectAllComponent) {
        newValue[newValue.length - 1].onChange();
        return;
      }
      props.onChange(isMulti ? newValue || [] : newValue)
    },
    [props.onChange, isMulti]
  )
  const versionClass = `${props?.version === 'v2' && props?.type !== 'secondary' ? 'Select__v2' : ''}`
  const canEditOptionClass = props?.version === 'v2' && props.canEditOption !== undefined ? 'Select--can-edit' : ''
  const classNames = cn(
    'Select',
    versionClass,
    canEditOptionClass,
    {
      'Select--inline': props.showMore,
      'Select--add-option': props.hasAddOption,
      'Select--error': props.error,
      'Select--secondary': props.type === 'secondary',
      'Select--selected': props.value
    },
    props.className
  )
  const valueLength = props.value ? props.value.length : 0;

  const presentState = (() => {
    if (props.options?.length === valueLength)
      return 'all'
    else if (valueLength > 0)
      return 'some'
    else
      return 'none';
  })()

  const onChangeSelectAll = () => {
    if (presentState !== 'all') {
      props.onChange(props.options);
    } else {
      props.onChange([])
    }
  }


  const getOptions = () => {
    if (props.isSelectAll && isMulti)
      return [{ label: `Select All (${props.options.length})`, value: '*', onChange: onChangeSelectAll, presentState: presentState, isSelectAllComponent: true }, ...props.options]
    return props.options;
  }

  return (
    <div className={classNames} style={{ ...widthStyleObj }} data-test-id={testId}>
      {props.selectLabel && <FieldLabel htmlFor="selectLabel">{props.selectLabel}</FieldLabel>}

      <Select
        id={props.id}
        styles={props.styles}
        menuPortalTarget={props.menuPortalTarget}
        name={props.name}
        closeMenuOnSelect={!isMulti}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isMulti={isMulti}
        isSearchable={isSearchable}
        placeholder={props.placeholder}
        hideSelectedOptions={hideSelectedOptions}
        closeMenuOnScroll={true}
        menuShouldBlockScroll={false}
        menuShouldScrollIntoView={props.menuShouldScrollIntoView}
        menuPlacement={props.menuPlacement}
        menuIsOpen={props.menuIsOpen}
        maxMenuHeight={maxMenuHeight}
        minMenuHeight={props.minMenuHeight}
        onChange={handleChange}
        onBlur={props.onBlur}
        options={getOptions()}
        menuClassName={props.menuClassName}
        menuStyle={props.menuStyle}
        value={props.value}
        // captureMenuScroll={false}
        components={allComponents}
        backspaceRemovesValue={false}
        classNamePrefix={props.menuPortalTarget ? 'Portal' : 'Select'}
        isNested={props.isNested}
        addOption={props.addOption}
        hasAddOption={props.hasAddOption}
        addOptionText={props.addOptionText}
        showCount={props.showCount}
        selectedLabel={props.selectedLabel}
        getOptionLabel={props.getOptionLabel}
        getOptionValue={props.getOptionValue}
        noCheckBox={props.noCheckBox}
        showMore={props.showMore}
        className={props.className}
        noOptionsMessage={props.noOptionsMessage}
        isOptionSelected={props.isOptionSelected}
        aria-label={'cs-select-aria'}
        filterOption={props.filterOption}
        isOptionDisabled={props.isOptionDisabled}
        onMenuOpen={props.onMenuOpen}
        onMenuClose={props.onMenuClose}
        tabSelectsValue={tabSelectsValue}
        onKeyDown={props.onKeyDown}
        ref={ref}
        isSelectAll={props.isSelectAll}
        multiDisplayLimit={props.multiDisplayLimit}
        isValueContainer={props.multiDisplayLimit ? true : false}
        version={props.version}
        showAllSelectedItems={showAllSelectedItems}
        setShowAllSelectedItems={setShowAllSelectedItems}
      />
    </div>
  )
})

const MemoizedSelectField = React.memo(SelectField)
MemoizedSelectField.displayName = 'Select'
export default MemoizedSelectField

// const Menu = (menuStyle?: object, menuClassName?: string) => {
//   return (props) => {
//     const { isDisabled, children, selectProps, ...rest } = props
//     return (
//       <components.Menu {...props}>
//         <div style={menuStyle}>
//           {children}
//           <div onClick={selectProps.addOption} className={`Select__add-option flex-v-center ${menuClassName}`}>{selectProps.addOptionText}</div>
//         </div>
//       </components.Menu>
//     )
//   }
// }

// const Menu = (props) => {
//   const { isDisabled, children, selectProps, ...rest } = props;
//   let menuClassName = "Select__add-option flex-v-center";
//   if (selectProps && selectProps.menuClassName) {
//     menuClassName = `${menuClassName} ${selectProps.menuClassName}`
//   }
//   return (
//     <components.Menu {...props}>
//       <div style={selectProps.menuStyle}>
//         {children}
//         <div onClick={selectProps.addOption} className={menuClassName}>{selectProps.addOptionText}</div>
//       </div>
//     </components.Menu>
//   );
// };
